import { motion, AnimatePresence } from "framer-motion";
import PlatformEventBanner from "@/components/game/PlatformEventBanner";
import HomeAdSense from "@/components/HomeAdSense";
import HomeTopAdSense from "@/components/HomeTopAdSense";
import SuggestionBox from "@/components/SuggestionBox";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import Leaderboard from "@/components/Leaderboard";
import { useIndexBoot } from "@/hooks/useIndexBoot";
const zizanioLogo = "/logo-zizanio.webp";
import { Menu, X, Share2, Link, Facebook, MessageCircle } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";


import gameTavliPhoto from "@/assets/game-tavli-photo.webp";
import kafeneioBanner from "@/assets/tavli-sto-zizanio.webp.asset.json";

import gameMahjongPhoto from "@/assets/game-mahjong-photo.png";
import strategoGeneralAsset from "@/assets/stratego-general-round.webp.asset.json";
const gameStrategoPhoto = strategoGeneralAsset.url;
import gameLudoPhoto from "@/assets/game-ludo-photo.webp";
import gameCardsPhoto from "@/assets/game-cards-photo.webp";
import gamePuzzlePhoto from "@/assets/game-puzzle-photo.webp";

import gameTennisHockeyPhoto from "@/assets/game-tennis-hockey-photo.webp";
import gameKremalaPhoto from "@/assets/game-kremala-photo.webp";
import gameChatPhoto from "@/assets/game-chat-photo.webp";
import PlayerSpotlight from "@/components/PlayerSpotlight";
import ZizanioMarketTeaser from "@/components/ZizanioMarketTeaser";
import { useHideCoinShop } from "@/hooks/useHideCoinShop";
import { SEO } from "@/components/SEO";
import zizanioHeroGames from "@/assets/zizanio-hero-games.png";

const games = [
  { name: "Τάβλι", icon: gameTavliPhoto, color: "bg-primary", comingSoon: false, path: "/tavli", isPhoto: true },
  {
    name: "Mahjong",
    icon: gameMahjongPhoto,
    color: "bg-secondary",
    comingSoon: false,
    path: "/mahjong",
    isPhoto: true,
  },
  {
    name: "Imperium 1800",
    icon: gameStrategoPhoto,
    color: "bg-playful-green",
    comingSoon: false,
    path: "/imperium1800",
    isPhoto: true,
  },
  {
    name: "Γκρινιάρης",
    icon: gameLudoPhoto,
    color: "bg-playful-blue",
    comingSoon: false,
    path: "/ludo",
    isPhoto: true,
  },
  {
    name: "Χαρτιά",
    icon: gameCardsPhoto,
    color: "bg-playful-purple",
    comingSoon: false,
    path: "/card-games",
    isPhoto: true,
  },
  { name: "Παζλ", icon: gamePuzzlePhoto, color: "bg-playful-yellow", comingSoon: true, isPhoto: true },
  {
    name: "Τένις & Χόκεϊ",
    icon: gameTennisHockeyPhoto,
    color: "bg-playful-blue",
    comingSoon: false,
    path: "/el/tennis-hockey/",
    isPhoto: true,
    external: true,
  },
  {
    name: "Κρεμάλα",
    icon: gameKremalaPhoto,
    color: "bg-playful-red",
    comingSoon: false,
    path: "/kremala",
    isPhoto: true,
  },
  { name: "Chat", icon: gameChatPhoto, color: "bg-playful-yellow", comingSoon: false, path: "/chat", isPhoto: true },
];

const GameIconOrbit = ({ game, index, total }: { game: (typeof games)[0]; index: number; total: number }) => {
  const navigate = useNavigate();
  const angle = (index * 360) / total - 90;
  const radius = 250;
  const x = Math.cos((angle * Math.PI) / 180) * radius;
  const y = Math.sin((angle * Math.PI) / 180) * radius;

  return (
    <motion.div
      className="absolute"
      style={{ left: `calc(50% + ${x}px - 48px)`, top: `calc(50% + ${y}px - 48px)` }}
      initial={{ opacity: 0, scale: 0, y: 40 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{
        type: "spring",
        stiffness: 260,
        damping: 12,
        delay: 0.3 + index * 0.08,
      }}
      whileHover={{ scale: 1.15, rotate: 5 }}
    >
      <GameIconContent game={game} />
    </motion.div>
  );
};

const GameIconGrid = ({ game, index }: { game: (typeof games)[0]; index: number }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.3, y: 30 }}
    animate={{ opacity: 1, scale: 1, y: 0 }}
    transition={{
      type: "spring",
      stiffness: 260,
      damping: 12,
      delay: 0.2 + index * 0.06,
    }}
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
  >
    <GameIconContent game={game} />
  </motion.div>
);

const GameIconContent = ({ game }: { game: (typeof games)[0] }) => {
  const navigate = useNavigate();
  return (
    <div
      className={`relative group ${game.comingSoon ? "cursor-default" : "cursor-pointer"}`}
      onClick={() => {
        if (game.comingSoon || !game.path) return;
        if ((game as any).external) {
          window.location.href = game.path;
        } else {
          navigate(game.path);
        }
      }}
    >
      <div
        className={`w-20 h-20 md:w-24 md:h-24 rounded-full ${game.isPhoto ? "" : game.color} shadow-lg flex items-center justify-center overflow-hidden border-4 border-white/30 transition-shadow group-hover:shadow-xl mx-auto`}
      >
        <img
          src={game.icon}
          alt={game.name}
          className={game.isPhoto ? "w-full h-full object-cover" : "w-14 h-14 md:w-16 md:h-16 object-contain"}
        />
      </div>
      <div className="mt-1 md:absolute md:-bottom-7 md:left-1/2 md:-translate-x-1/2 whitespace-nowrap text-center">
        <span className="font-display text-xs md:text-sm font-semibold text-white">{game.name}</span>
      </div>
      {game.comingSoon && (
        <div className="absolute -top-1 -right-1 md:-right-1 bg-accent text-[hsl(340,70%,15%)] text-[10px] font-bold px-2 py-0.5 rounded-full font-display">
          Σύντομα
        </div>
      )}
    </div>
  );
};

const Index = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { data: isAdmin } = useIsAdmin();
  const { data: bootData, isLoading: bootLoading } = useIndexBoot();
  const hideCoinShop = useHideCoinShop();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showMarketTeaser, setShowMarketTeaser] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const isRegistered = Boolean(user && !user.is_anonymous && (user.email || user.user_metadata?.email));

  const shareUrl = "https://zizanio.gr/";
  const shareText = "Παίξε δωρεάν τάβλι, Imperium 1800, Ludo, Κρεμάλα και άλλα παιχνίδια στο Zizanio.gr!";

  const handleShare = async () => {
    if (typeof navigator !== "undefined" && (navigator as any).share) {
      try {
        await (navigator as any).share({ title: "Zizanio.gr", text: shareText, url: shareUrl });
        return;
      } catch {
        // fallback
      }
    }
    setShareOpen(true);
  };

  const handleCopyLink = () => {
    navigator.clipboard?.writeText(shareUrl).then(() => {
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    });
  };


  const handleMarketClick = () => {
    if (isRegistered) {
      navigate("/chat/shop");
    } else {
      setShowMarketTeaser(true);
    }
  };

  return (
    <>
      <SEO
        title="Zizanio.gr | Τάβλι, Imperium 1800, Ξερή, Αγωνία"
        description="Παίξε δωρεάν Τάβλι, Imperium 1800, Γκρινιάρη, Ξερή και Αγωνία online με Έλληνες παίκτες. Ζωντανό chat, mini-games και κατάταξη."
        canonical="https://zizanio.gr/"
        keywords="zizanio, ελληνικά παιχνίδια online, τάβλι online, imperium 1800, γκρινιάρης, ξερή, αγωνία, ελληνικό chat, δωρεάν επιτραπέζια, multiplayer"
        ogTitle="Zizanio.gr – Ελληνικό Chat | Δωρεάν Επιτραπέζια Online"
        ogDescription="Παίξε Τάβλι, Imperium 1800, Γκρινιάρη, Ξερή και Αγωνία με Έλληνες παίκτες. Ζωντανό chat και κοινότητα."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Zizanio.gr",
          url: "https://zizanio.gr",
          description: "Ελληνική πλατφόρμα ψυχαγωγίας: chat, επιτραπέζια παιχνίδια, mini games.",
          inLanguage: "el",
          potentialAction: {
            "@type": "SearchAction",
            target: "https://zizanio.gr/?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }}
      />
      <main
        className="min-h-screen overflow-hidden"
        style={{
          background: "linear-gradient(180deg, hsl(216, 75%, 25%) 0%, hsl(210, 70%, 45%) 50%, hsl(200, 65%, 60%) 100%)",
        }}
      >
        {/* Header */}
        <header className="relative z-10 pt-8 text-center">
          {/* Top-left About link */}
          <div className="absolute top-4 left-4 z-50">
            <motion.button
              onClick={() => navigate("/info-leaderboard")}
              className="bg-white/15 text-white font-display font-bold text-sm px-4 py-2 rounded-full border border-white/20 backdrop-blur-sm hover:bg-white/25 transition-all"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              🏆 Τι είναι η Κατάταξη
            </motion.button>
          </div>

          {/* Desktop buttons */}
          <div className="absolute top-4 right-4 hidden md:flex gap-2">
            <motion.button
              onClick={handleShare}
              className="bg-white/15 text-white font-display font-bold text-sm px-4 py-2 rounded-full border border-white/20 shadow-md hover:bg-white/25 hover:shadow-lg transition-all flex items-center gap-1.5"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              aria-label="Κοινοποίηση"
            >
              <Share2 className="w-4 h-4" />
              Κοινοποίηση
            </motion.button>
            {user ? (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <motion.button
                      onClick={() => navigate("/chat/app")}
                      aria-label="Chat games – συνομιλία για εύρεση αντιπάλων και έναρξη παιχνιδιών"
                      className="bg-secondary text-secondary-foreground font-display font-bold text-sm px-5 py-2 rounded-full shadow-md hover:shadow-lg transition-shadow"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      🎮 Chat games
                    </motion.button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="rounded text-xs max-w-[260px] text-center leading-snug">
                    Αυτό είναι chat μόνο για τα παιχνίδια. Εδώ μπορούν οι παίκτες παιχνιδιών να συναντηθούν και να
                    συνδεθούν να παίξουν μεταξύ τους πολύ πιο εύκολα σε κάθε παιχνίδι multiplayer
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <motion.button
                      onClick={() => navigate("/chat")}
                      aria-label="Chat – συνομιλία και γνωριμίες"
                      className="bg-white/15 text-white font-display font-bold text-sm px-5 py-2 rounded-full shadow-md hover:shadow-lg transition-shadow border border-white/20 hover:bg-white/25"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      👋 Chat
                    </motion.button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="rounded text-xs max-w-[260px] text-center leading-snug">
                    Αυτό είναι το κανονικό τσατ για γνωριμίες και φιλίες με άλλους χρήστες από την Ελλάδα
                  </TooltipContent>
                </Tooltip>
                <motion.button
                  onClick={() => navigate("/profile")}
                  className="bg-primary text-primary-foreground font-display font-bold text-sm px-5 py-2 rounded-full shadow-md hover:shadow-lg transition-shadow"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  👤 Προφίλ
                </motion.button>
                {isAdmin && (
                  <motion.button
                    onClick={() => navigate("/admin")}
                    className="bg-destructive text-destructive-foreground font-display font-bold text-sm px-5 py-2 rounded-full shadow-md hover:shadow-lg transition-shadow"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    🛡️ Admin
                  </motion.button>
                )}
                <motion.button
                  onClick={signOut}
                  className="bg-white/15 text-white font-body font-semibold text-sm px-4 py-2 rounded-full border border-white/20 shadow-sm hover:shadow-md hover:bg-white/25 transition-all"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Αποσύνδεση
                </motion.button>
              </>
            ) : (
              <motion.button
                onClick={() => navigate("/auth")}
                className="bg-primary text-primary-foreground font-display font-bold text-sm px-5 py-2 rounded-full shadow-md hover:shadow-lg transition-shadow"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                🔑 Σύνδεση
              </motion.button>
            )}
          </div>

          {/* Mobile hamburger */}
          <div className="absolute top-4 right-4 md:hidden z-50">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Μενού πλοήγησης"
              className="bg-white/15 text-white p-2 rounded-lg border border-white/20 backdrop-blur-sm"
            >
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>

            <AnimatePresence>
              {menuOpen && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9, y: -10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9, y: -10 }}
                  transition={{ duration: 0.15 }}
                  className="absolute top-12 right-0 bg-card/95 backdrop-blur-md border border-border rounded-xl shadow-xl p-2 min-w-[160px] space-y-1"
                >
                  <button
                    onClick={() => {
                      handleShare();
                      setMenuOpen(false);
                    }}
                    className="w-full text-left text-sm font-semibold px-3 py-2.5 rounded-lg hover:bg-muted transition-colors text-foreground flex items-center gap-2"
                  >
                    <Share2 className="w-4 h-4 shrink-0" />
                    Κοινοποίηση
                  </button>
                  <div className="border-t border-border my-1" />
                  {user ? (
                    <>
                      <button
                        onClick={() => {
                          navigate("/chat/app");
                          setMenuOpen(false);
                        }}
                        className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-muted transition-colors text-foreground"
                      >
                        <div className="text-sm font-semibold">🎮 Chat games</div>
                        <div className="text-[11px] text-muted-foreground leading-tight mt-0.5">
                          Chat μόνο για τα παιχνίδια — συνδέσου εύκολα με άλλους παίκτες για multiplayer
                        </div>
                      </button>
                      <button
                        onClick={() => {
                          navigate("/chat");
                          setMenuOpen(false);
                        }}
                        className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-muted transition-colors text-foreground"
                      >
                        <div className="text-sm font-semibold">👋 Chat</div>
                        <div className="text-[11px] text-muted-foreground leading-tight mt-0.5">
                          Το κανονικό τσατ για γνωριμίες και φιλίες με άλλους χρήστες από την Ελλάδα
                        </div>
                      </button>
                      <button
                        onClick={() => {
                          navigate("/profile");
                          setMenuOpen(false);
                        }}
                        className="w-full text-left text-sm font-semibold px-3 py-2.5 rounded-lg hover:bg-muted transition-colors text-foreground"
                      >
                        👤 Προφίλ
                      </button>
                      {isAdmin && (
                        <button
                          onClick={() => {
                            navigate("/admin");
                            setMenuOpen(false);
                          }}
                          className="w-full text-left text-sm font-semibold px-3 py-2.5 rounded-lg hover:bg-muted transition-colors text-destructive"
                        >
                          🛡️ Admin
                        </button>
                      )}
                      <div className="border-t border-border my-1" />
                      <button
                        onClick={() => {
                          signOut();
                          setMenuOpen(false);
                        }}
                        className="w-full text-left text-sm font-semibold px-3 py-2.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground"
                      >
                        🚪 Αποσύνδεση
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => {
                        navigate("/auth");
                        setMenuOpen(false);
                      }}
                      className="w-full text-left text-sm font-semibold px-3 py-2.5 rounded-lg hover:bg-muted transition-colors text-foreground"
                    >
                      🔑 Σύνδεση
                    </button>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="flex justify-center">
            <img
              src={zizanioLogo}
              alt="Zizanio.gr"
              className="h-28 md:h-40 object-contain"
              width={500}
              height={280}
              fetchPriority="high"
            />
          </div>
          <HomeTopAdSense />

          <p className="font-body text-lg md:text-xl text-white/70 mt-2 font-semibold">
            Το Zizanio ειναι ο χώρος που μπορείς να κάνεις συνομιλία chat με αλλους από όλη την ελλάδα 🎲
          </p>
          <p className="font-body text-sm md:text-base text-white/50 mt-2 max-w-xl mx-auto italic">
            Κάνε νέους φίλους με συνομιλία σε πραγματικό χρόνο στο ελληνικό τσατ. Videochat. Παίξε τα καλύτερα παιχνίδια
            σε multiplayer εκδοχή δωρεάν και άμεσα χωρίς περιορισμούς. Παίξε τώρα{" "}
            <a
              href="/tavli"
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                e.preventDefault();
                navigate("/tavli");
                window.scrollTo(0, 0);
              }}
              className="text-white/70 hover:text-primary underline transition-colors"
            >
              τάβλι
            </a>
            ,{" "}
            <a
              href="/imperium1800"
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                e.preventDefault();
                navigate("/imperium1800");
                window.scrollTo(0, 0);
              }}
              className="text-white/70 hover:text-primary underline transition-colors"
            >
              Imperium 1800
            </a>
            ,{" "}
            <a
              href="/ludo"
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                e.preventDefault();
                navigate("/ludo");
                window.scrollTo(0, 0);
              }}
              className="text-white/70 hover:text-primary underline transition-colors"
            >
              Ludo
            </a>
            ,{" "}
            <a
              href="/kremala"
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                e.preventDefault();
                navigate("/kremala");
                window.scrollTo(0, 0);
              }}
              className="text-white/70 hover:text-primary underline transition-colors"
            >
              Κρεμάλα
            </a>
            ,{" "}
            <a
              href="/agonia"
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                e.preventDefault();
                navigate("/agonia");
                window.scrollTo(0, 0);
              }}
              className="text-white/70 hover:text-primary underline transition-colors"
            >
              Αγωνία
            </a>
            . Καλή διασκέδαση!
          </p>
        </header>

        {/* Mobile: Grid layout */}
        <section className="relative z-10 md:hidden px-6 mt-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 150 }}
            className="flex justify-center mb-6"
          >
            <PlayerSpotlight spotlightData={bootData?.spotlight} isLoading={bootLoading} />
          </motion.div>
          <div className="grid grid-cols-2 gap-4">
            {games.map((game, i) => (
              <GameIconGrid key={game.name} game={game} index={i} />
            ))}
          </div>
        </section>

        {/* Desktop: Orbit layout */}
        <section className="relative z-10 hidden md:flex items-center justify-center mt-16">
          <div className="relative" style={{ width: 600, height: 600 }}>
            <div className="absolute inset-[50px] rounded-full border-2 border-dashed border-white/20 opacity-40" />
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 ml-[24px] mt-[40px]">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 150 }}
              >
                <PlayerSpotlight spotlightData={bootData?.spotlight} isLoading={bootLoading} />
              </motion.div>
            </div>
            {games.map((game, i) => (
              <GameIconOrbit key={game.name} game={game} index={i} total={games.length} />
            ))}
          </div>
        </section>

        {/* Kafeneio banner */}
        <section className="relative z-10 px-4 mt-8">
          <a
            href="/tavli"
            className="block max-w-3xl mx-auto rounded-2xl overflow-hidden border-2 border-white/20 shadow-2xl group focus:outline-none focus:ring-4 focus:ring-primary/40"
            aria-label="Τάβλι στο Zizanio.gr — παίξε τώρα"
          >
            <img
              src={kafeneioBanner.url}
              alt="Τάβλι στο Zizanio.gr — το καθημερινό σου στέκι για πόρτες, πλακωτό, φεύγα και παιχνίδια με φίλους"
              width={1200}
              height={800}
              loading="lazy"
              decoding="async"
              className="w-full h-auto transition-transform duration-300 group-hover:scale-[1.02]"
            />
          </a>
        </section>

        {/* AdSense slot under game icons */}
        <HomeAdSense />


        {/* CTA Section */}
        <section className="relative z-10 text-center mt-16 pb-16 px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="max-w-md mx-auto"
          >
            <h2 className="font-display text-2xl md:text-3xl font-bold text-white mb-4">Η παρέα σε περιμένει!</h2>
            <p className="font-body text-white/70 mb-6">
              Κλασικά επιτραπέζια, online με φίλους. Βρες αντίπαλο, ανέβα στην κατάταξη, κέρδισε βραβεία!
            </p>
            <img
              src={zizanioHeroGames}
              alt="Zizanio.gr - Παίξε με άλλους online"
              loading="lazy"
              className="w-full max-w-sm mx-auto mb-6 rounded-2xl shadow-2xl"
            />
            <motion.button
              className="bg-primary text-[hsl(25,60%,10%)] font-display font-bold text-lg px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition-shadow"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate("/paixnidia")}
            >
              🎮 Ξεκίνα να παίζεις!
            </motion.button>

            <div className="flex flex-wrap justify-center gap-3 mt-4">
              <motion.a
                href="https://zizanio.gr/paixnidia"
                className="bg-white/15 text-white font-display font-bold text-sm px-5 py-2.5 rounded-full border border-white/20 backdrop-blur-sm hover:bg-white/25 transition-all"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                🎲 Βασικά παιχνίδια
              </motion.a>
              <motion.a
                href="https://games.zizanio.gr/"
                className="bg-white/15 text-white font-display font-bold text-sm px-5 py-2.5 rounded-full border border-white/20 backdrop-blur-sm hover:bg-white/25 transition-all"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                🎯 Όλα τα παιχνίδια
              </motion.a>
            </div>

            <p className="text-sm text-white/50 mt-3 font-body">Δωρεάν εγγραφή · Χωρίς κατέβασμα</p>

            {/* Zizanio Market Button */}
            {hideCoinShop === false && (
              <motion.button
                className="mt-6 bg-gradient-to-r from-[hsl(45,90%,55%)] to-[hsl(25,85%,50%)] text-[hsl(220,40%,13%)] font-display font-bold text-lg px-8 py-4 rounded-full shadow-lg hover:shadow-xl hover:brightness-110 transition-all"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleMarketClick}
              >
                🏪 Zizanio Market
              </motion.button>
            )}
          </motion.div>
        </section>

        {/* Features strip */}
        <section
          className="relative z-10 py-12 px-4"
          style={{ background: "linear-gradient(180deg, hsl(216, 75%, 20%) 0%, hsl(216, 70%, 28%) 100%)" }}
        >
          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            {[
              { emoji: "🏆", title: "Πίνακες Κατάταξης", desc: "Ανέβα στην κορυφή και γίνε θρύλος!" },
              { emoji: "👥", title: "Online Multiplayer", desc: "Παίξε με φίλους ή βρες αντίπαλο" },
              { emoji: "🎨", title: "Προσωποποίηση", desc: "Avatars, θέματα και badges μοναδικά για σένα" },
            ].map((feat, i) => (
              <motion.div
                key={feat.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="space-y-2"
              >
                <div className="text-4xl">{feat.emoji}</div>
                <h3 className="font-display text-xl font-bold text-white">{feat.title}</h3>
                <p className="font-body text-white/60 text-sm">{feat.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Leaderboard */}
        <section className="relative z-10 py-12 px-4">
          <div className="max-w-lg mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-white text-center mb-6">
                🏆 Κορυφαίοι Παίκτες
              </h2>
              <Leaderboard leaderboardData={bootData?.leaderboard} isLoading={bootLoading} />
              <motion.button
                onClick={() => navigate("/leaderboard")}
                className="mt-4 w-full bg-white/10 text-white border border-white/20 font-display font-semibold text-sm py-3 rounded-full hover:bg-white/20 transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Δες όλη την κατάταξη →
              </motion.button>
            </motion.div>
          </div>
        </section>

        {/* ═══════════════ SEO FOOTER ═══════════════ */}
        <footer className="relative z-10 py-10 px-4 border-t border-white/10 bg-[hsl(220,40%,13%)]">
          <div className="max-w-5xl mx-auto">
            {/* SEO keyword links — real <a> tags so Google can crawl them */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-6 gap-y-3 mb-8">
              {[
                { label: "Τάβλι Online", path: "/tavli" },
                { label: "Τάβλι Πόρτες", path: "/tavli" },
                { label: "Τάβλι Πλακωτό", path: "/tavli" },
                { label: "Τάβλι Φεύγα", path: "/tavli" },
                { label: "Multiplayer Τάβλι", path: "/tavli" },
                { label: "Παίξε Τάβλι Online", path: "/tavli" },
                { label: "Τάβλι με Φίλους", path: "/tavli" },
                { label: "Backgammon Online", path: "/tavli" },
                { label: "Imperium 1800 Online", path: "/imperium1800" },
                { label: "Imperium 1800 Multiplayer", path: "/imperium1800" },
                { label: "Παίξε Imperium 1800 Δωρεάν", path: "/imperium1800" },
                { label: "Imperium 1800 με Φίλους", path: "/imperium1800" },
                { label: "Στρατηγείο Imperium 1800", path: "/imperium1800/stratigeio" },
                { label: "Imperium 1800 Preview", path: "/imperium1800/preview" },
                { label: "Γκρινιάρης Online", path: "/ludo" },
                { label: "Ludo Online", path: "/ludo" },
                { label: "Αγωνία Χαρτιά", path: "/agonia" },
                { label: "Ξερή Online", path: "/kseri" },
                { label: "Παιχνίδια με χαρτιά", path: "/card-games" },
                { label: "Ελληνικό Chat", path: "/chat" },
                { label: "Επιτραπέζια Online", path: "/" },
                { label: "Δωρεάν Παιχνίδια", path: "/" },
                { label: "Ελληνικά Παιχνίδια", path: "/" },
              ].map((kw) => (
                <a
                  key={kw.label}
                  href={kw.path}
                  onClick={(e) => {
                    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                    e.preventDefault();
                    navigate(kw.path);
                    window.scrollTo(0, 0);
                  }}
                  className="text-left font-body text-xs text-white/60 hover:text-primary transition-colors"
                >
                  {kw.label}
                </a>
              ))}
            </div>

            {/* Secondary links */}
            <div className="flex flex-wrap justify-center gap-4 mb-6 text-xs font-body text-white/60">
              <a
                href="/about"
                onClick={(e) => {
                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                  e.preventDefault();
                  navigate("/about");
                  window.scrollTo(0, 0);
                }}
                className="hover:text-white/60 transition-colors"
              >
                Τι είναι το Zizanio
              </a>
              <span>·</span>
              <a
                href="/privacy"
                onClick={(e) => {
                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                  e.preventDefault();
                  navigate("/privacy");
                  window.scrollTo(0, 0);
                }}
                className="hover:text-white/60 transition-colors"
              >
                Πολιτική Απορρήτου
              </a>
              <span>·</span>
              <a
                href="/terms"
                onClick={(e) => {
                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                  e.preventDefault();
                  navigate("/terms");
                  window.scrollTo(0, 0);
                }}
                className="hover:text-white/60 transition-colors"
              >
                Όροι Χρήσης
              </a>
              <span>·</span>
              <a
                href="/leaderboard"
                onClick={(e) => {
                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                  e.preventDefault();
                  navigate("/leaderboard");
                  window.scrollTo(0, 0);
                }}
                className="hover:text-white/60 transition-colors"
              >
                Κατάταξη
              </a>
            </div>

            {/* Copyright */}
            <p className="text-center text-xs text-white/60 font-body">
              © 2026 zizanio.gr — Φτιαγμένο με ❤️ για συνομιλία τσατ και gaming μαζι
            </p>
          </div>
        </footer>
        <ZizanioMarketTeaser open={showMarketTeaser} onClose={() => setShowMarketTeaser(false)} />
        <SuggestionBox />

        {/* Floating share button — mobile-friendly, visible label */}
        <motion.button
          onClick={() => {
            if (typeof navigator !== "undefined" && (navigator as any).share) {
              (navigator as any).share({ title: "Zizanio.gr", text: shareText, url: shareUrl }).catch(() => {});
            } else {
              setShareOpen((o) => !o);
            }
          }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          aria-label="Κοινοποίησε το Zizanio"
          className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-50 h-12 pl-3 pr-4 rounded-full bg-primary text-primary-foreground shadow-2xl flex items-center gap-2 border-2 border-white/30 hover:shadow-primary/40 hover:border-white/50 transition-all ring-2 ring-white/20"
          style={{ bottom: "calc(1rem + env(safe-area-inset-bottom))" }}
        >
          <Share2 className="w-5 h-5" />
          <span className="font-display font-bold text-sm whitespace-nowrap">Κοινοποίηση</span>
        </motion.button>

        <AnimatePresence>
          {shareOpen && (
            <motion.div
              key="share-menu"
              initial={{ opacity: 0, scale: 0.8, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 10 }}
              transition={{ duration: 0.15 }}
              className="fixed bottom-16 right-4 md:bottom-20 md:right-6 z-50 bg-card/95 backdrop-blur-md border border-border rounded-2xl shadow-2xl p-3 w-52"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-display font-bold text-white">Κοινοποίηση</span>
                <button
                  onClick={() => setShareOpen(false)}
                  className="text-white/60 hover:text-white text-xs px-1"
                  aria-label="Κλείσιμο"
                >
                  ✕
                </button>
              </div>
              <div className="space-y-1">
                <button
                  onClick={handleCopyLink}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-muted text-white/90 text-sm text-left transition-colors"
                >
                  {shareCopied ? (
                    <span className="text-green-400">✅ Αντιγράφηκε</span>
                  ) : (
                    <>
                      <Link className="w-4 h-4 shrink-0" />
                      Αντιγραφή συνδέσμου
                    </>
                  )}
                </button>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-muted text-white/90 text-sm transition-colors"
                  onClick={() => setShareOpen(false)}
                >
                  <Facebook className="w-4 h-4 shrink-0" />
                  Facebook
                </a>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-muted text-white/90 text-sm transition-colors"
                  onClick={() => setShareOpen(false)}
                >
                  <MessageCircle className="w-4 h-4 shrink-0" />
                  WhatsApp
                </a>
                <a
                  href={`viber://forward?text=${encodeURIComponent(shareText + " " + shareUrl)}`}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-muted text-white/90 text-sm transition-colors"
                  onClick={() => setShareOpen(false)}
                >
                  <MessageCircle className="w-4 h-4 shrink-0" />
                  Viber
                </a>
                <a
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-muted text-white/90 text-sm transition-colors"
                  onClick={() => setShareOpen(false)}
                >
                  <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                  X / Twitter
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </>
  );
};


export default Index;
